/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { closeDashboardDialog } from '@actions/dashboard-actions';
import {
  addToGroups,
  createNewGroupWithEntities,
  fetchGroupsData
} from '@actions/groups-actions';
import { getEntityTypeLabel, getGroupTypesConfig } from '@constants/config';
import * as dialog from '@constants/dialogs';
import Button from '@material-ui/core/Button';
import { Checkbox } from '@mui';
import { groupListById } from '@selectors/groups-selector';
import Dialog from '@shared/dialogs/dialog';
import AddToGroup from '@shared/add-to-group';
import {
  findGroupType,
  prepareGroupsForBulkAdd
} from '@utils/group-utils';
import { isAppAdmin, canEditGroups } from '@utils/permission-utils';

const getDefaultGroupType = () => {
  const groupTypes = getGroupTypesConfig().filter(groupType => canEditGroups(groupType.name));
  if (groupTypes && groupTypes.length > 0) {
    return groupTypes[0].id;
  }
  return null;
};

const AddToGroupDialog = () => {
  const dispatch = useDispatch();

  // Redux state
  const activeDialogs = useSelector(state => state.dashboard.activeDialogs);
  const agencyId = useSelector(state => state.auth.user.agencyId);
  const dialogData = useSelector(state => state.groups.dialog);
  const groupsById = useSelector(groupListById);
  const preFill = useSelector(state => state.groups.preFill);

  const [groupType, setGroupType] = useState(preFill ? preFill.type : getDefaultGroupType());
  const [includeOverlaps, setIncludeOverlaps] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState(preFill ? [preFill.id] : []);

  useEffect(() => {
    // Only load groups if we selected an agency, or we are super users:
    if (agencyId || isAppAdmin()) {
      dispatch(fetchGroupsData());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = () => dispatch(closeDashboardDialog(dialog.ADD_TO_GROUP));

  const handleCheckboxChange = () => setIncludeOverlaps(!includeOverlaps);

  // Returns true if the selected group type requires area capture:
  const isAreaCapture = () => {
    const selectedGroupType = findGroupType(groupType);
    return selectedGroupType.area_capture > 0;
  };

  const getIncludeOverlaps = () => {
    if (isAreaCapture()) {
      return includeOverlaps;
    }
    return false;
  };

  const createGroup = () => {
    const { addDataId, addDataType } = dialogData;
    const selectedGroupType = findGroupType(groupType);
    const entityIds = {
      [addDataType]: [addDataId]
    };
    dispatch(closeDashboardDialog(dialog.ADD_TO_GROUP));
    dispatch(createNewGroupWithEntities(entityIds, selectedGroupType.name, getIncludeOverlaps()));
  };

  const handleSubmit = () => {
    const { addDataId, addDataType, addCallback } = dialogData;
    const payload = prepareGroupsForBulkAdd({
      selectedGroups,
      entityIds: [addDataId],
      includeOverlaps: getIncludeOverlaps()
    });
    const selectedGroupType = findGroupType(groupType);
    dispatch(addToGroups(selectedGroupType.name, payload, addDataType, false, null, false, addCallback));
  };

  const getActions = () => {
    const active = selectedGroups.length > 0 && dialogData.addDataId && dialogData.addDataType;
    const areaCapture = isAreaCapture();

    const list = [];
    if (!areaCapture) {
      list.push(<Button color="primary" onClick={createGroup}>NEW GROUP</Button>);
      // Add separator to make "New Group" button appear on the left.
      list.push(<div style={{ flex: '1 0 0' }} />);
    }
    return [
      ...list,
      <Button
        key="cancel"
        onClick={handleCancel}
        style={{ marginRight: '1rem' }}
      >
        CANCEL
      </Button>,
      <Button
        key="add"
        color="primary"
        disabled={!active}
        onClick={handleSubmit}
        variant="contained"
      >
        ADD
      </Button>
    ];
  };

  if (!activeDialogs[dialog.ADD_TO_GROUP]) {
    return null;
  }

  const areaCapture = isAreaCapture();

  return (
    <Dialog
      actions={getActions()}
      title="Add to group"
    >
      <AddToGroup
        existingGroups={dialogData.addDataGroups}
        groupsById={groupsById}
        groupType={groupType}
        selectedGroups={selectedGroups}
        setGroupType={setGroupType}
        setSelectedGroups={setSelectedGroups}
      />
      {areaCapture && (
        <Checkbox
          checked={getIncludeOverlaps()}
          label={`Include all conflicts of the ${getEntityTypeLabel(dialogData.addDataType)}`}
          onChange={handleCheckboxChange}
        />
      )}
    </Dialog>
  );
};

export default AddToGroupDialog;
